import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { FromStringToDate, ToMoneyFormat, debounce } from "../../utils/Helper";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

interface InputMoneyProps {
  label: string;
  value?: number;
  disabled?: boolean;
  onChange(value: number): void;
  size?: "small" | "medium";
  variant?: "standard" | "outlined" | "filled";
}

export interface IOptions {
  text: string;
  value: any;
}

interface InputAutocompleteProps {
  options: InputAutocompleteOptions[];
  label: string;
  loading?: boolean;
  onChange(item: InputAutocompleteOptions | null): void;
  onSearch?(text: string): void;
  defaultValue?: any;
  value?: any;
  disabled?: boolean;
  width?: number;
  clearAfterInput?: boolean;
}

export interface InputAutocompleteOptions {
  label: string;
  id: any;
}

const InputMoney = ({
  value,
  onChange,
  label,
  disabled,
  size,
  variant,
}: InputMoneyProps) => {
  const [amount, setAmount] = useState<string>("");

  useEffect(() => {
    if (value) {
      const target = value.toString().replaceAll(".", "");
      const temp = ToMoneyFormat(target);
      setAmount(temp || "");
    } else {
      setAmount("");
    }
  }, [value]);

  function handleOnChage(e: string) {
    const target = e.replaceAll(".", "");
    const temp = ToMoneyFormat(target);
    setAmount(temp || "");
    onChange(target ? parseInt(target) : 0);
  }

  return (
    <TextField
      variant={variant}
      size={size}
      value={amount}
      label={label}
      fullWidth
      onChange={(e) => handleOnChage(e.target.value)}
      disabled={disabled}
    />
  );
};

const InputSelect = ({
  id,
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  id: string | number;
  label: string;
  value: any;
  options: IOptions[];
  disabled?: boolean;
  onChange(item: any, collection: IOptions): void;
}) => {
  function handleChange(e: any) {
    const temp = options.filter((item) => item.value === e);
    onChange(e, temp[0]);
  }

  return (
    <FormControl style={{ width: "100%" }}>
      <InputLabel id={`select-input-${id}`}>{label}</InputLabel>
      <Select
        value={value}
        labelId={`select-input-${id}`}
        id={`select-input-${id}`}
        fullWidth
        label={label}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((item, index) => {
          return (
            <MenuItem key={index} value={item.value}>
              {item.text}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const NewAutoComplete = ({
  width,
  options,
  label,
  onChange,
  defaultValue,
  onSearch,
  value,
  loading,
  disabled,
  clearAfterInput,
}: InputAutocompleteProps) => {
  const [stateValue, setStateValue] = useState<InputAutocompleteOptions | null>(
    null
  );

  useEffect(() => {
    if (options && value) {
      //console.log(value);
      // const temp = { ...value };
      // setStateValue(temp);
    }
  }, [value, options]);

  const handleOnChange = debounce((value: string) => {
    if (onSearch) {
      onSearch(value);
    }
  }, 250);

  return (
    <Box>
      <Autocomplete
        disabled={disabled}
        loading={loading}
        fullWidth
        disablePortal
        options={options}
        sx={{ width: width }}
        value={stateValue}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            onChange={(e) => handleOnChange(e.target.value)}
          />
        )}
        onChange={(e, v) => {
          onChange(v);
        }}
      />
    </Box>
  );
};

const AutoCompleteSelect = ({
  width,
  options,
  label,
  onChange,
  onSearch,
  value,
  loading,
  disabled,
}: InputAutocompleteProps) => {
  return (
    <Box>
      <Autocomplete
        defaultValue={value}
        disabled={disabled}
        loading={loading}
        fullWidth
        disablePortal
        options={options}
        sx={{ width: width }}
        value={value === null ? { id: null, label: "" } : value}
        renderInput={(params) => (
          <TextField
            {...params}
            value={"baru"}
            label={label}
            onChange={(e) => onSearch && onSearch(e.target.value)}
          />
        )}
        onChange={(e, v) => onChange(v)}
      />
    </Box>
  );
};

interface IInputDate {
  value?: string;
  label?: string;
  onChange(date: string): void;
}

const InputDate = ({ onChange, value, label }: IInputDate) => {
  const [date, setDate] = useState<any>();

  useEffect(() => {
    if (value) {
      // Convert ISO → Jakarta date
      const d = new Date(value);

      // Force Jakarta timezone (+7)
      const jakartaDate = new Date(d.getTime() + 7 * 60 * 60 * 1000);
      const parsedDate = dayjs(jakartaDate);
      setDate(parsedDate);
    }
  }, [value]);

  const handleOnChange = (targetValue: any) => {
    const raw = targetValue?.$d;

    // --- Validation: must exist ---
    if (!raw) return;

    const dateObj = new Date(raw);

    // --- Validation: must be valid date ---
    if (isNaN(dateObj.getTime())) return;

    // --- Validation: year must be >= 2000 ---
    const year = dateObj.getFullYear();
    if (year < 2000) return;

    const offset = dateObj.getTimezoneOffset(); // difference in minutes between local and UTC
    const jakarta = new Date(dateObj.getTime() + 7 * 60 * 60 * 1000);
    const date = jakarta.toISOString().slice(0, -1);
    const valueDate = FromStringToDate(date, true);
    onChange(date);
  };

  return (
    <Box width={"100%"}>
      <DatePicker
        sx={{ width: "100%" }}
        format="DD/MM/YYYY"
        label={label}
        value={date}
        onChange={(e: any) => handleOnChange(e)}
      />
    </Box>
  );
};

interface IInputRangeData {
  startDate?: string;
  endDate?: string;
  startLabel?: string;
  endLabel?: string;
  onChange(startDate: string, endDate: string): void;
}

const InputRangeDate = ({
  startLabel,
  startDate,
  endLabel,
  endDate,
  onChange,
}: IInputRangeData) => {
  const [startDateValue, setStartDateValue] = useState<any>();
  const [endDateValue, setEndDateValue] = useState<any>();

  useEffect(() => {
    if (startDateValue || endDateValue) {
      onChange(startDateValue, endDateValue);
    }
  }, [startDateValue, endDateValue]);

  function handleChangeInput(e: any, type: "start" | "end") {
    const raw = e?.$d;

    // --- Validation: must exist ---
    if (!raw) return;

    const dateObj = new Date(raw);

    // --- Validation: must be valid date ---
    if (isNaN(dateObj.getTime())) return;

    // --- Validation: year must be >= 2000 ---
    const year = dateObj.getFullYear();
    if (year < 2000) return;

    const local = new Date(e.$d);
    const offset = local.getTimezoneOffset(); // difference in minutes between local and UTC
    const jakarta = new Date(local.getTime() - offset * 60000);
    const date = jakarta.toISOString().slice(0, -1);
    if (type === "start") {
      setStartDateValue(date);
    } else {
      setEndDateValue(date);
    }
  }

  return (
    <Box display={"flex"} gap={1}>
      <DatePicker
        label={startLabel || "Tanggal Awal"}
        onChange={(e) => handleChangeInput(e, "start")}
        format="D-MM-YYYY"
        // value={new Date()}
        value={startDateValue}
      />
      <DatePicker<Dayjs>
        label={endLabel || "Tanggal Ahkir"}
        onChange={(e) => handleChangeInput(e, "end")}
        format="D-MM-YYYY"
        value={endDateValue as any}
        minDate={dayjs(startDateValue)}
      />
    </Box>
  );
};

export {
  InputMoney,
  InputSelect,
  AutoCompleteSelect,
  InputRangeDate,
  NewAutoComplete,
  InputDate,
};
